import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Skeleton } from "./ui/skeleton";
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Banknote, Wallet, BarChart3, ShieldCheck, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const fmtEur = (v) => {
  if (v == null || isNaN(v)) return "-";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
};
const fmtNum = (v, dec = 2) => v == null || isNaN(v) ? "-" : Number(v).toFixed(dec);
const fmtPct = (v, dec = 1) => v == null || isNaN(v) ? "-" : `${Number(v).toFixed(dec)}%`;

const RATING_COLORS = {
  A: { bg: "bg-emerald-500", border: "border-emerald-300", text: "text-emerald-900", soft: "bg-emerald-50" },
  B: { bg: "bg-blue-500", border: "border-blue-300", text: "text-blue-900", soft: "bg-blue-50" },
  C: { bg: "bg-amber-500", border: "border-amber-300", text: "text-amber-900", soft: "bg-amber-50" },
  D: { bg: "bg-orange-500", border: "border-orange-300", text: "text-orange-900", soft: "bg-orange-50" },
  E: { bg: "bg-red-500", border: "border-red-300", text: "text-red-900", soft: "bg-red-50" },
};

const PRIORITY_BADGES = {
  positive: { bg: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
  high: { bg: "bg-red-100 text-red-800 border-red-300", icon: AlertTriangle },
  medium: { bg: "bg-amber-100 text-amber-800 border-amber-300", icon: Info },
  low: { bg: "bg-slate-100 text-slate-800 border-slate-300", icon: Info },
};

const ScoreBar = ({ label, score, max, hint }) => {
  const pct = (score / max) * 100;
  const color = pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="space-y-1" data-testid={`score-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex justify-between text-xs">
        <span className="font-medium text-slate-700">{label} <span className="text-slate-400 font-normal">({hint})</span></span>
        <span className="font-bold tabular-nums">{score}/{max}</span>
      </div>
      <div className="h-2 bg-slate-200 rounded overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const MetricCard = ({ icon: Icon, label, value, subline, accent, testid }) => (
  <Card className={`${accent || 'border-slate-200'}`} data-testid={testid}>
    <CardContent className="pt-3 pb-3">
      <div className="flex items-start gap-2">
        <Icon className="h-4 w-4 text-slate-500 mt-1 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide truncate">{label}</div>
          <div className="text-lg font-bold tabular-nums">{value}</div>
          {subline && <div className="text-[10px] text-slate-500 mt-0.5">{subline}</div>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const BankRatingPage = ({ clientId, year }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios.get(`${API}/clients/${clientId}/bank-rating?year=${year}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || "Errore caricamento rating"))
      .finally(() => setLoading(false));
  }, [clientId, year]);

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-6 text-center text-red-700">{error}</CardContent>
      </Card>
    );
  }

  if (!data) return null;
  const { metrics: m, scores, rating, rating_label, suggestions, sp_year_used, sp_month_used, regime, data_warnings = [] } = data;
  const ratingColors = RATING_COLORS[rating] || RATING_COLORS.C;
  const isSemplificata = regime === 'semplificata';
  const usingFallback = !isSemplificata && (sp_year_used !== year || !sp_month_used);

  // Score bars differ between regimes
  const scoreBars = isSemplificata
    ? [
        { label: "DSCR", score: scores.dscr, max: 25, hint: "EBITDA / rata mutui" },
        { label: "Mesi sopravvivenza", score: scores.mesi_sopravvivenza, max: 20, hint: "liquidita / costi fissi" },
        { label: "EBITDA Margin", score: scores.ebitda_margin, max: 15, hint: "redditivita" },
        { label: "Andamentale conto", score: scores.andamentale, max: 15, hint: "trend liquidita" },
        { label: "Crescita ricavi", score: scores.crescita_ricavi, max: 10, hint: "YoY" },
        { label: "Anzianita attivita", score: scores.anzianita, max: 10, hint: "anni attivi" },
        { label: "Interest Coverage", score: scores.interest_coverage, max: 5, hint: "EBITDA / oneri fin." },
      ]
    : data?.cash_business
    ? [
        { label: "PFN/EBITDA", score: scores.pfn_ebitda, max: 25, hint: "sostenibilita debito" },
        { label: "Liquidita Corrente", score: scores.liquidita_corrente, max: 35, hint: "AC/PC (peso aumentato per cash business)" },
        { label: "Indebitamento", score: scores.indebitamento, max: 15, hint: "capitalizzazione" },
        { label: "EBITDA Margin", score: scores.ebitda_margin, max: 15, hint: "redditivita" },
        { label: "Rotazione CI", score: scores.rotazione_ci, max: 10, hint: "produttivita asset" },
      ]
    : [
        { label: "PFN/EBITDA", score: scores.pfn_ebitda, max: 25, hint: "sostenibilita debito" },
        { label: "Liquidita Corrente", score: scores.liquidita_corrente, max: 20, hint: "AC/PC" },
        { label: "Quick Ratio", score: scores.quick_ratio, max: 15, hint: "acid test" },
        { label: "Indebitamento", score: scores.indebitamento, max: 15, hint: "capitalizzazione" },
        { label: "EBITDA Margin", score: scores.ebitda_margin, max: 15, hint: "redditivita" },
        { label: "Rotazione CI", score: scores.rotazione_ci, max: 10, hint: "produttivita asset" },
      ];

  return (
    <div className="space-y-4" data-testid="bank-rating-page">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-2xl font-bold text-slate-900">Rating Bancario {year}</h2>
          <Badge className={isSemplificata ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-blue-100 text-blue-800 border-blue-300"}>
            {isSemplificata ? "Contabilita Semplificata" : "Contabilita Ordinaria"}
          </Badge>
          {data?.cash_business && (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300" data-testid="cash-business-badge">
              💵 Attività Cash (no crediti commerciali)
            </Badge>
          )}
        </div>
        <p className="text-sm text-slate-600">
          {isSemplificata
            ? "Calcolato usando CE, estratti conto e mutui (senza Stato Patrimoniale)."
            : "Come la banca legge il tuo bilancio: indici di solidita, liquidita e sostenibilita del debito."}
        </p>
      </div>

      {usingFallback && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-3 text-xs text-amber-900 flex items-center gap-2">
            <Info className="h-4 w-4 flex-shrink-0" />
            <span>
              Stato Patrimoniale per {year} non disponibile. Utilizzo SP del {sp_month_used ? `mese ${sp_month_used}/${sp_year_used}` : `anno ${sp_year_used}`}
              {m.bank_liquidity_from_statements != null && ` + saldi banca al ${m.bank_liquidity_date}`}.
            </span>
          </CardContent>
        </Card>
      )}

      {data_warnings.length > 0 && (
        <Card className="border-rose-200 bg-rose-50/60">
          <CardContent className="py-3 space-y-1">
            <div className="text-xs font-bold text-rose-900 uppercase tracking-wider mb-1">⚠ Avvisi sui dati</div>
            {data_warnings.map((w, i) => (
              <div key={i} className="text-xs text-rose-800 flex items-start gap-1.5">
                <AlertTriangle className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${w.severity === 'high' ? 'text-rose-600' : w.severity === 'medium' ? 'text-amber-600' : 'text-slate-500'}`} />
                <span>{w.message}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Rating legend */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Legenda Rating</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            {[
              { l: 'A', range: '>= 80', label: 'Eccellente', desc: 'Bancabile a tassi vantaggiosi. La banca ti propone le condizioni migliori.' },
              { l: 'B', range: '65-79', label: 'Buono', desc: 'Facilmente bancabile. Accesso al credito senza problemi a tassi standard.' },
              { l: 'C', range: '45-64', label: 'Mediocre', desc: 'Bancabile ma con garanzie aggiuntive (fideiussione, pegno, ipoteca).' },
              { l: 'D', range: '25-44', label: 'Critico', desc: 'Difficolta di accesso al credito. La banca chiede tassi alti o rifiuta.' },
              { l: 'E', range: '< 25', label: 'Insufficiente', desc: 'Necessario rifinanziamento o ristrutturazione del debito.' },
            ].map((r, i) => {
              const c = RATING_COLORS[r.l];
              const isCurrent = rating === r.l;
              return (
                <div key={i} className={`border-2 rounded p-2 ${c.border} ${isCurrent ? c.soft : 'bg-white'} ${isCurrent ? 'ring-2 ring-offset-1 ring-slate-300' : ''}`}>
                  <div className={`flex items-center gap-1.5 ${c.text}`}>
                    <span className="text-2xl font-black">{r.l}</span>
                    <div className="flex flex-col">
                      <span className="font-bold text-xs">{r.label}</span>
                      <span className="text-[9px] text-slate-500">{r.range}/100</span>
                    </div>
                    {isCurrent && <Badge className="ml-auto text-[9px] bg-slate-700">TU</Badge>}
                  </div>
                  <div className="text-[10px] text-slate-600 leading-tight mt-1">{r.desc}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Hero rating card */}
      <Card className={`${ratingColors.border} border-2 ${ratingColors.soft}`} data-testid="rating-hero">
        <CardContent className="py-6">
          <div className="grid md:grid-cols-3 gap-4 items-center">
            <div className="text-center md:text-left">
              <div className="text-xs text-slate-500 uppercase font-medium tracking-wider">Rating</div>
              <div className="flex items-baseline gap-3">
                <div className={`text-7xl font-black ${ratingColors.text}`}>{rating}</div>
                <div className="text-2xl font-bold tabular-nums text-slate-700">{scores.total}<span className="text-base text-slate-400">/{scores.max_total}</span></div>
              </div>
              <div className={`text-sm font-medium ${ratingColors.text} mt-1`}>{rating_label}</div>
            </div>
            <div className="md:col-span-2 space-y-2">
              {scoreBars.map((b, i) => (
                <ScoreBar key={i} label={b.label} score={b.score || 0} max={b.max} hint={b.hint} />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key metrics row */}
      {isSemplificata ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard icon={BarChart3} label="EBITDA" value={fmtEur(m.ebitda)} subline={m.ebitda_margin_pct != null ? `${fmtPct(m.ebitda_margin_pct)} dei ricavi` : null} accent="border-blue-200 bg-blue-50/30" testid="m-ebitda" />
          <MetricCard
            icon={Banknote}
            label="DSCR (Sostenibilita Rate)"
            value={m.dscr != null ? `${fmtNum(m.dscr, 2)}x` : (m.rata_annua === 0 ? "Nessun mutuo" : "-")}
            subline={m.dscr != null ? (m.dscr >= 3 ? "Eccellente" : m.dscr >= 1.5 ? "Bancabile" : "Critico") : (m.rata_annua === 0 ? "" : "")}
            accent={m.dscr >= 3 || m.rata_annua === 0 ? "border-emerald-200 bg-emerald-50/30" : m.dscr >= 1.5 ? "border-blue-200 bg-blue-50/30" : "border-red-200 bg-red-50/30"}
            testid="m-dscr"
          />
          <MetricCard
            icon={Wallet}
            label="Mesi di Sopravvivenza"
            value={m.mesi_sopravvivenza != null ? fmtNum(m.mesi_sopravvivenza, 1) : "-"}
            subline={m.mesi_sopravvivenza != null ? (m.mesi_sopravvivenza >= 3 ? "Solido" : m.mesi_sopravvivenza >= 1 ? "Limitato" : "Critico") : ""}
            accent={m.mesi_sopravvivenza >= 3 ? "border-emerald-200 bg-emerald-50/30" : m.mesi_sopravvivenza >= 1 ? "border-amber-200 bg-amber-50/30" : "border-red-200 bg-red-50/30"}
            testid="m-mesi"
          />
          <MetricCard
            icon={ShieldCheck}
            label="Debito / EBITDA"
            value={m.debt_to_ebitda != null ? `${fmtNum(m.debt_to_ebitda, 2)}x` : (m.debito_residuo === 0 ? "0" : "-")}
            subline={`Debito residuo ${fmtEur(m.debito_residuo)}`}
            accent={m.debt_to_ebitda <= 2 || m.debito_residuo === 0 ? "border-emerald-200 bg-emerald-50/30" : "border-amber-200 bg-amber-50/30"}
            testid="m-debt-ebitda"
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard icon={BarChart3} label="EBITDA" value={fmtEur(m.ebitda)} subline={m.ebitda_margin_pct != null ? `${fmtPct(m.ebitda_margin_pct)} dei ricavi` : null} accent="border-blue-200 bg-blue-50/30" testid="m-ebitda" />
        <MetricCard
          icon={m.posizione_finanziaria_netta < 0 ? TrendingDown : TrendingUp}
          label="Posizione Finanziaria Netta"
          value={fmtEur(m.posizione_finanziaria_netta)}
          subline={m.posizione_finanziaria_netta < 0 ? "Cassa netta positiva" : "Debito netto"}
          accent={m.posizione_finanziaria_netta < 0 ? "border-emerald-200 bg-emerald-50/30" : "border-amber-200 bg-amber-50/30"}
          testid="m-pfn"
        />
        <MetricCard
          icon={ShieldCheck}
          label="Indice Liquidita"
          value={m.indice_liquidita_corrente != null ? (m.indice_liquidita_corrente >= 999 ? "∞" : fmtNum(m.indice_liquidita_corrente, 2)) : "-"}
          subline={m.indice_liquidita_corrente != null ? (m.indice_liquidita_corrente >= 999 ? "Nessun debito a breve" : m.indice_liquidita_corrente >= 1.5 ? "Ottimo (>= 1.5)" : m.indice_liquidita_corrente >= 1 ? "Accettabile" : "Critico (< 1)") : "Dati SP mancanti"}
          accent={m.indice_liquidita_corrente >= 1.5 ? "border-emerald-200 bg-emerald-50/30" : m.indice_liquidita_corrente >= 1 ? "border-amber-200 bg-amber-50/30" : "border-red-200 bg-red-50/30"}
          testid="m-liq"
        />
        <MetricCard
          icon={Wallet}
          label="PFN / EBITDA"
          value={m.pfn_ebitda != null ? `${fmtNum(m.pfn_ebitda, 2)}x` : "-"}
          subline={m.pfn_ebitda == null ? "" : m.pfn_ebitda <= 0 ? "Eccellente" : m.pfn_ebitda <= 3 ? "Bancabile" : m.pfn_ebitda <= 5 ? "Pesante" : "Insostenibile"}
          accent={m.pfn_ebitda <= 0 ? "border-emerald-200 bg-emerald-50/30" : m.pfn_ebitda <= 3 ? "border-blue-200 bg-blue-50/30" : "border-amber-200 bg-amber-50/30"}
          testid="m-pfn-ebitda"
        />
        </div>
      )}

      {/* Detailed indicators table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Dettaglio indicatori</CardTitle>
          <CardDescription>{isSemplificata ? "Indicatori calcolati da CE, estratti conto e mutui (regime semplificato)" : "Voci aggregate dello Stato Patrimoniale e indici calcolati"}</CardDescription>
        </CardHeader>
        <CardContent>
          {isSemplificata ? (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <div className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Conto Economico (annualizzato)</div>
                {[
                  { label: "Ricavi annui", v: fmtEur(m.revenue_annual_estimated) },
                  { label: "EBITDA annua", v: fmtEur(m.ebitda_annual_estimated) },
                  { label: "Costi fissi mensili", v: fmtEur(m.fixed_costs_monthly) },
                  { label: "Oneri finanziari annui", v: fmtEur(m.financial_charges_annual) },
                  { label: "Liquidita banca", v: fmtEur(m.liquidity_latest), subv: m.liquidity_latest_date ? `al ${m.liquidity_latest_date}` : null },
                  { label: "Rata mutui mensile", v: fmtEur(m.rata_mensile_totale) },
                  { label: "Debito residuo (mutui)", v: fmtEur(m.debito_residuo) },
                  { label: "Anzianita attivita", v: m.years_active != null ? `${m.years_active} anni` : "-" },
                ].map((row, i) => (
                  <div key={i} className="flex justify-between text-sm border-b border-slate-100 py-1">
                    <div>
                      <div className="text-slate-700">{row.label}</div>
                      {row.subv && <div className="text-[10px] text-slate-400">{row.subv}</div>}
                    </div>
                    <span className="font-semibold tabular-nums">{row.v}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-1.5">
                <div className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Indici (Banca)</div>
                {[
                  { label: "DSCR (EBITDA / Rata)", v: m.dscr != null ? `${fmtNum(m.dscr, 2)}x` : (m.rata_annua === 0 ? "Nessun mutuo" : "-"), threshold: m.dscr >= 3 || m.rata_annua === 0 ? 'good' : m.dscr >= 1.5 ? 'medium' : 'bad', target: ">= 1.5x" },
                  { label: "Mesi sopravvivenza", v: m.mesi_sopravvivenza != null ? `${fmtNum(m.mesi_sopravvivenza, 1)} mesi` : "-", threshold: m.mesi_sopravvivenza >= 3 ? 'good' : m.mesi_sopravvivenza >= 1 ? 'medium' : 'bad', target: ">= 3 mesi" },
                  { label: "EBITDA Margin %", v: fmtPct(m.ebitda_margin_pct), threshold: m.ebitda_margin_pct >= 10 ? 'good' : 'bad', target: ">= 10%" },
                  { label: "Debito / EBITDA", v: m.debt_to_ebitda != null ? `${fmtNum(m.debt_to_ebitda, 2)}x` : (m.debito_residuo === 0 ? "0" : "-"), threshold: m.debt_to_ebitda <= 2 || m.debito_residuo === 0 ? 'good' : m.debt_to_ebitda <= 4 ? 'medium' : 'bad', target: "<= 3x" },
                  { label: "Interest Coverage", v: m.interest_coverage != null ? `${fmtNum(m.interest_coverage, 1)}x` : "Nessun onere", threshold: m.interest_coverage >= 5 ? 'good' : m.interest_coverage >= 3 ? 'medium' : 'bad', target: ">= 3x" },
                  { label: "Crescita ricavi YoY", v: m.growth_yoy_pct != null ? fmtPct(m.growth_yoy_pct) : "n/a (anno base)", threshold: m.growth_yoy_pct >= 0 ? 'good' : m.growth_yoy_pct >= -5 ? 'medium' : 'bad', target: ">= 0%" },
                  { label: "Andamentale conto", v: m.andamentale_factor != null ? fmtPct(m.andamentale_factor * 100) : "-", threshold: m.andamentale_factor >= 0.6 ? 'good' : m.andamentale_factor >= 0.4 ? 'medium' : 'bad', target: ">= 60%" },
                ].map((row, i) => (
                  <div key={i} className="flex justify-between items-center text-sm border-b border-slate-100 py-1">
                    <div>
                      <div className="text-slate-700">{row.label}</div>
                      {row.target && <div className="text-[10px] text-slate-400">target {row.target}</div>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold tabular-nums">{row.v}</span>
                      {row.threshold === 'good' && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
                      {row.threshold === 'medium' && <Info className="h-3.5 w-3.5 text-amber-500" />}
                      {row.threshold === 'bad' && <AlertTriangle className="h-3.5 w-3.5 text-red-500" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <div className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Bilancio aggregato</div>
              {[
                { label: "Liquidita immediate", v: m.liquidita },
                { label: "Attivo corrente", v: m.attivo_corrente },
                { label: "Passivo corrente", v: m.passivo_corrente },
                { label: "Magazzino", v: m.magazzino },
                { label: "Capitale investito netto", v: m.capitale_investito },
                { label: "Patrimonio netto", v: m.patrimonio_netto },
                { label: "Debiti fin. (totali)", v: m.debiti_finanziari_totali, subv: `breve ${fmtEur(m.debiti_finanziari_breve)} · lungo ${fmtEur(m.debiti_finanziari_lungo)}` },
              ].map((row, i) => (
                <div key={i} className="flex justify-between text-sm border-b border-slate-100 py-1">
                  <div>
                    <div className="text-slate-700">{row.label}</div>
                    {row.subv && <div className="text-[10px] text-slate-400">{row.subv}</div>}
                  </div>
                  <span className="font-semibold tabular-nums">{fmtEur(row.v)}</span>
                </div>
              ))}
            </div>
            <div className="space-y-1.5">
              <div className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Indici</div>
              {[
                { label: "Ricavi", v: fmtEur(m.revenue), score: null },
                { label: "EBITDA Margin %", v: fmtPct(m.ebitda_margin_pct), threshold: m.ebitda_margin_pct >= 10 ? 'good' : 'bad', target: ">= 10%" },
                { label: "PFN / EBITDA", v: m.pfn_ebitda != null ? `${fmtNum(m.pfn_ebitda, 2)}x` : "-", threshold: m.pfn_ebitda != null && m.pfn_ebitda <= 3 ? 'good' : 'bad', target: "<= 3x" },
                { label: "Indice Liq. Corrente (AC/PC)", v: m.indice_liquidita_corrente >= 999 ? "∞" : fmtNum(m.indice_liquidita_corrente, 2), threshold: m.indice_liquidita_corrente >= 1.5 ? 'good' : m.indice_liquidita_corrente >= 1 ? 'medium' : 'bad', target: ">= 1.5" },
                { label: "Quick Ratio", v: m.quick_ratio >= 999 ? "∞" : fmtNum(m.quick_ratio, 2), threshold: m.quick_ratio >= 1 ? 'good' : 'bad', target: ">= 1" },
                { label: "% Indebitamento", v: fmtPct(m.indebitamento_pct), threshold: m.indebitamento_pct < 60 ? 'good' : m.indebitamento_pct < 70 ? 'medium' : 'bad', target: "< 60%" },
                { label: "Rotazione Cap. Inv.", v: fmtNum(m.rotazione_capitale_investito, 2), threshold: m.rotazione_capitale_investito >= 1 ? 'good' : 'medium', target: ">= 1" },
              ].map((row, i) => (
                <div key={i} className="flex justify-between items-center text-sm border-b border-slate-100 py-1">
                  <div>
                    <div className="text-slate-700">{row.label}</div>
                    {row.target && <div className="text-[10px] text-slate-400">target {row.target}</div>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold tabular-nums">{row.v}</span>
                    {row.threshold === 'good' && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
                    {row.threshold === 'medium' && <Info className="h-3.5 w-3.5 text-amber-500" />}
                    {row.threshold === 'bad' && <AlertTriangle className="h-3.5 w-3.5 text-red-500" />}
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}
        </CardContent>
      </Card>

      {/* Suggestions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Come migliorare il rating</CardTitle>
          <CardDescription>Azioni concrete per aumentare il punteggio e l'attrattivita verso le banche</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {suggestions.length === 0 ? (
            <div className="text-sm text-slate-500 italic">Nessun suggerimento prioritario - il bilancio appare in buona salute.</div>
          ) : suggestions.map((s, i) => {
            const cfg = PRIORITY_BADGES[s.priority] || PRIORITY_BADGES.low;
            const Icon = cfg.icon;
            return (
              <div key={i} className={`border-l-4 ${s.priority === 'high' ? 'border-red-400' : s.priority === 'medium' ? 'border-amber-400' : s.priority === 'positive' ? 'border-emerald-400' : 'border-slate-300'} pl-3 py-2 bg-slate-50/40 rounded-r`} data-testid={`suggestion-${i}`}>
                <div className="flex items-start gap-2">
                  <Icon className={`h-4 w-4 mt-0.5 ${s.priority === 'high' ? 'text-red-500' : s.priority === 'medium' ? 'text-amber-500' : s.priority === 'positive' ? 'text-emerald-500' : 'text-slate-500'}`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-semibold text-sm">{s.title}</span>
                      <Badge className={`text-[10px] ${cfg.bg} font-medium`} variant="outline">
                        {s.priority === 'positive' ? 'PUNTO DI FORZA' : s.priority === 'high' ? 'PRIORITA ALTA' : s.priority === 'medium' ? 'PRIORITA MEDIA' : 'PRIORITA BASSA'}
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-600 leading-snug">{s.detail}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card className="border-slate-200 bg-slate-50/40">
        <CardContent className="py-3 text-[10px] text-slate-500">
          {isSemplificata ? (
            <>
              <b>Metodologia (Contabilita Semplificata):</b> 7 indicatori che le banche calcolano per ditte individuali / professionisti senza Stato Patrimoniale formale.
              Punteggio: DSCR 25 + Mesi sopravvivenza 20 + EBITDA Margin 15 + Andamentale conto 15 + Crescita ricavi 10 + Anzianita 10 + Interest Coverage 5 = 100.
              Soglie: A {`>= 80`}, B 65-79, C 45-64, D 25-44, E {`< 25`}.
            </>
          ) : (
            <>
              <b>Metodologia (Contabilita Ordinaria):</b> Il rating combina 6 indici utilizzati dalle banche italiane per la valutazione PMI.
              Punteggio: PFN/EBITDA 25 + Liquidita Corrente 20 + Quick Ratio 15 + Indebitamento 15 + EBITDA Margin 15 + Rotazione CI 10 = 100.
              Soglie: A {`>= 80`}, B 65-79, C 45-64, D 25-44, E {`< 25`}.
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BankRatingPage;
